import { Injectable } from '@angular/core';
import * as _ from 'lodash';
import { GfTranslateService } from "../../../core/services/core.translate.service";
import { UserService } from '../../../core/services/core.user.service';
import { UserRest } from '../../../core/services/core.user.rest.service';
import { UiService } from '../../../core/services/core.ui.service';
import { NotificationService } from '../../../core/services/core.notification.service';
import { TrackeritemService } from './trackeritem.service';

@Injectable()
export class TrackeritemQueueService {
	private queue: any = [];

    constructor(private userService: UserService, private userRest: UserRest, private uiService: UiService, 
	private trackeritemService: TrackeritemService, private translateService: GfTranslateService, private notificationService: NotificationService) {
	}

	/**
	 * Adds a tracker item to the queue
	 * @param {object} tracker item
	 * @returns {array}
	 */
	addTrackerItemToQueue = (trackerItem) => {
		return new Promise((resolve, reject) => {
			if (!this.userService.session.hasUser()) {
				reject(new Error());
				return;
			}

			this.trackeritemService.setAssignee(trackerItem, this.userService.session.getUser())
			.then(() => {
				this.userRest.postQueue(this.userService.session.getUnixName(), {tracker_item_id:trackerItem.id})
					.then((response) => {
						this.uiService.message.showSuccess("Added #"+trackerItem.id+" to your queue", "Queue Updated");
						this.queue = _.union(this.queue, [trackerItem.id]);
						this.notificationService.notifyAll('queue-change', this.queue);
						resolve(this.queue);
					}, () => {
						this.uiService.message.showWarning("Sorry, could not add #"+trackerItem.id+" to your queue", "Queue Update Failed");
						reject(new Error());
					});
			}, (err: any) => {
				this.uiService.message.showWarning("Sorry, could not add #"+trackerItem.id+" to your queue", "Queue Update Failed");
				reject(err);
			});
		});
	}

	/**
	 * Loads user queue from the backend
	 */
	getQueue = () => {
		return new Promise((resolve, reject) => {
			if (!this.userService.session.hasUser()) {
				reject(false);
				return;
			}
	
			this.userRest.getQueue(this.userService.session.getUnixName())
				.then((response) => {
					var tiList = _.get(response, 'items', []);
					this.queue = _.map(tiList, 'id');
					resolve(this.queue);
				}, (err) => {
					this.uiService.message.showError('Could not get user queue', 'User Queue');
					reject(err);
				});
		});
	}

	/**
	 * Given a trackerItem object, checks if the session user is assigned to it
	 * @param {object}  tracker item
	 * @returns {boolean}
	 */
	isAssignedToCurrentUser = (trackerItem) => {
		if (_.has(trackerItem.rel, 'assignees')) {
			if (_.isObject(_.find(trackerItem.rel.assignees, {id: this.userService.session.getUserId()}))) {
				return true;
			}
			return false;
		}
		return false;
	}

	/**
	 * Given a tracker item object, checks if it's already in the queue
	 * @param {object} tracker item
	 * @returns {boolean}
	 */	
	inQueue = (trackerItem) => {
		return (this.queue.indexOf(trackerItem.id) >= 0);
	}

	/**
	 * Given a tracker item, checks if the tracker item can be added/removed to/from the user queue
	 * @param {object} tracker item to add to the queue
	 * @param {boolean}} allowAssignments on this tracker item
	 * @returns {boolean}
	 */
	canUpdateQueue = (trackerItem, allowAssignments) => {
		allowAssignments = typeof allowAssignments === 'boolean' ? allowAssignments : false;
		return (allowAssignments || this.isAssignedToCurrentUser(trackerItem) || this.inQueue(trackerItem))
	}

	/**
	 * Removes a tracker item from the queue
	 * @param (object) tracker item
	 * @returns {boolean} true: request succesful, false otherwise
	 */
	removeTrackerItemFromQueue = (trackerItem) => {
		return new Promise((resolve, reject) => {
			if (!this.userService.session.hasUser()) {
				reject();
				return;
			}
	
			return this.userRest.removeFromQueue(this.userService.session.getUnixName(), {tracker_item_id:trackerItem.id})
				.then((response) => {
					this.uiService.message.showSuccess("Removed #"+trackerItem.id+" from your queue", "Queue Updated");
					this.queue = _.without(this.queue, trackerItem.id);
					this.notificationService.notifyAll('queue-change', this.queue);
					resolve(null);
				}, () => {
					this.uiService.message.showWarning("Sorry, could not remove #"+trackerItem.id+" from your queue", "Queue Update Failed");
					reject();
				});
		});
	}

	/**
	 * Given a tracker item, adds it to the queue if it's not already there, otherwise, it removes it from the queue
	 * @param {object} tracker item
	 */
	toggleQueue = (trackerItem, canAssign) => {
		if (this.canUpdateQueue(trackerItem, canAssign)) {
			if (this.inQueue(trackerItem)) {
				return this.removeTrackerItemFromQueue(trackerItem);
			} else {
				return this.addTrackerItemToQueue(trackerItem);
			}
		}
	}

	/**
	 * Retrieves the next TID from the queue
	 * @param {int} tracker item id
	 * @returns {int}
	 */
	getNextId = (tid) => {
		return _.without(this.queue, tid)[0];
	}

	/**
	 * Adds TID to the queue
	 * @param {int} tracker item id
	 */
	add = (tid) => {
		this.queue = _.union(this.queue, [tid]);
	}

	/**
	 * Removes TID from the queue
	 * @param {int} tracker item id
	 */
	remove = (tid) => {
		this.queue = _.without(this.queue, tid);
	}

	/**
	 * Retrieves the user queue from the backend and updates local queue
	 */
	updateQueue = () => {
		return new Promise((resolve, reject) => {
			if (this.userService.session.getUserId() == this.userService.constants.nobody.id) {
				reject();
				return;
			}
	
			this.userRest.getQueue(this.userService.session.getUnixName(), {})
			.then((response: any) => {
				this.queue = _.map(_.get(response, 'items', []), 'id');
				this.notificationService.notifyAll('queue-change', this.queue);
				resolve(this.queue);
			})
			.catch((response) => {
				this.uiService.message.showError(response, 'User Queue');
				reject(null);
			});
		});
	}

	/**
	 * Adds and persists a TID to the user queue
	 * @param {int} tracker item id
	 */
	addToQueue = (tid) => {
		return new Promise((resolve, reject) => {
			this.userRest.postQueue(this.userService.session.getUnixName(), { tracker_item_id : tid })
			.then((response) => {
				var addedQMsg = this.translateService.translate('Added #{{item}} to your queue', { item : tid}),
					successMsg = this.translateService.translate('To-Do Updated');

					this.uiService.message.showSuccess(addedQMsg, successMsg);
					this.add(tid);
					resolve(this.queue);
			}, (response) => {
				var addedQMsg = this.translateService.translate('Sorry, could not add #{{item}} to your To-Do', {item: tid}),
					errorMsg = this.translateService.translate('To-Do Update Failed');

					this.uiService.message.showWarning(addedQMsg, errorMsg);
					reject(null);
			});
		});
	}

	/**
	 * Removes and persists a TID from the queue
	 * @param {int} tracker item id
	 */
	removeFromQueue = (tid, silent) => {
		return new Promise((resolve, reject) => {
			this.userRest.removeFromQueue(this.userService.session.getUnixName(), {tracker_item_id: tid})
			.then((response) => {
				var removedMsg = this.translateService.translate('Removed #{{item}} from your To-Do', { item: tid});
				var queueUpdatedMsg = this.translateService.translate('To-Do Updated');

				if (!silent) {
					this.uiService.message.showSuccess(removedMsg, queueUpdatedMsg);
				}
				this.remove(tid);
				resolve(this.queue);
			}, (response) => {
				var removedMsg = this.translateService.translate('Sorry, could not remove #{{item}} from your To-Do', { item: tid}),
					queueUpdatedMsg = this.translateService.translate('To-Do Update Failed');

				this.uiService.message.showWarning(removedMsg, queueUpdatedMsg);
				reject(null);
			});
		});
		
	}
}
